import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

export interface CartItem {
  id: number;
  name: string;
  price: number;
  industry_name: string;
  industry_color: string;
}

interface CartContextType {
  items: CartItem[];
  addItem: (item: CartItem) => void;
  removeItem: (itemId: number) => void;
  clearCart: () => void;
  itemCount: number;
  totalPrice: number;
  isInCart: (itemId: number) => boolean;
  isLoading: boolean;
}

const CartContext = createContext<CartContextType | undefined>(undefined);

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};

interface CartProviderProps {
  children: ReactNode;
}

export const CartProvider: React.FC<CartProviderProps> = ({ children }) => {
  const [items, setItems] = useState<CartItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  // Load cart from localStorage on mount
  useEffect(() => {
    const savedCart = localStorage.getItem('testinvest_cart');
    console.log('Loading cart from localStorage:', savedCart);
    if (savedCart) {
      try {
        const parsedCart = JSON.parse(savedCart);
        console.log('Parsed cart:', parsedCart);
        setItems(parsedCart);
      } catch (error) {
        console.error('Failed to load cart from localStorage:', error);
      }
    }
    setIsLoading(false);
  }, []);

  // Save cart to localStorage whenever items change (but only after initial load)
  useEffect(() => {
    if (!isLoading) {
      localStorage.setItem('testinvest_cart', JSON.stringify(items));
      console.log('Saving cart to localStorage:', items);
    }
  }, [items, isLoading]);

  const addItem = React.useCallback((newItem: CartItem) => {
    setItems(prevItems => {
      // Check if item already exists
      if (prevItems.some(item => item.id === newItem.id)) {
        return prevItems; // Don't add duplicate
      }
      return [...prevItems, newItem];
    });
  }, []);

  const removeItem = React.useCallback((itemId: number) => {
    setItems(prevItems => prevItems.filter(item => item.id !== itemId));
  }, []);

  const clearCart = React.useCallback(() => {
    setItems([]);
  }, []);

  const isInCart = React.useCallback((itemId: number) => {
    return items.some(item => item.id === itemId);
  }, [items]);

  const itemCount = React.useMemo(() => items.length, [items]);
  const totalPrice = React.useMemo(() => 
    items.reduce((total, item) => total + Number(item.price), 0), 
    [items]
  );

  // Memoize the context value to prevent unnecessary re-renders
  const contextValue = React.useMemo(() => ({
    items,
    addItem,
    removeItem,
    clearCart,
    itemCount,
    totalPrice,
    isInCart,
    isLoading,
  }), [items, addItem, removeItem, clearCart, itemCount, totalPrice, isInCart, isLoading]);

  return (
    <CartContext.Provider value={contextValue}>
      {children}
    </CartContext.Provider>
  );
};